import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Navigation } from './components/Navigation';
import { useGlobalSettings } from './hooks/useGlobalSettings';
import { isOrgAdmin } from './utils/roles';
import ErrorBoundary from './components/ErrorBoundary';
import './styles/globals.css';

// Função de retry para imports dinâmicos - resolve problema de cache após deploy
const lazyWithRetry = <T extends React.ComponentType<unknown>>(
  importFn: () => Promise<{ default: T }>,
  retries = 3
): React.LazyExoticComponent<T> => {
  return lazy(async () => {
    for (let i = 0; i < retries; i++) {
      try {
        return await importFn();
      } catch (error) {
        if (i === retries - 1) {
          // Última tentativa falhou - força reload da página para buscar novos assets
          console.error('Failed to load module after retries, reloading page...', error);
          window.location.reload();
          // Retorna uma promise que nunca resolve para evitar erro durante reload
          return new Promise(() => {});
        }
        // Aguarda um pouco antes de tentar novamente
        await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
      }
    }
    throw new Error('Failed to load module');
  });
};

const ContactsPage = lazyWithRetry(() => import('./pages/ContactsPage').then(module => ({ default: module.ContactsPage })));
const WhatsAppConnectionsPage = lazyWithRetry(() => import('./pages/WhatsAppConnectionsPage').then(module => ({ default: module.WhatsAppConnectionsPage })));
const AtendimentoPage = lazyWithRetry(() => import('./pages/AtendimentoPage').then(module => ({ default: module.AtendimentoPage })));
const CampaignsPage = lazyWithRetry(() => import('./pages/CampaignsPage').then(module => ({ default: module.CampaignsPage })));
const SettingsPage = lazyWithRetry(() => import('./pages/SettingsPage').then(module => ({ default: module.SettingsPage })));
const ProfilePage = lazyWithRetry(() => import('./pages/ProfilePage').then(module => ({ default: module.ProfilePage })));
const UsersPage = lazyWithRetry(() => import('./pages/UsersPage').then(module => ({ default: module.UsersPage })));
const OrganizationsPage = lazyWithRetry(() => import('./pages/OrganizationsPage').then(module => ({ default: module.OrganizationsPage })));
const LoginPage = lazyWithRetry(() => import('./pages/LoginPage').then(module => ({ default: module.LoginPage })));
const AdminDashboard = lazyWithRetry(() => import('./pages/AdminDashboard'));
const UserDashboard = lazyWithRetry(() => import('./pages/UserDashboard'));
const ConnectionHelpPage = lazyWithRetry(() => import('./pages/ConnectionHelpPage').then(module => ({ default: module.ConnectionHelpPage })));

function AppContent() {
  const { isAuthenticated, user } = useAuth();
  const { settings } = useGlobalSettings();
  const isAdminUser = isOrgAdmin(user?.role);
  const fallback = (
    <div className="flex flex-1 items-center justify-center p-6 text-slate-500">
      Carregando...
    </div>
  );

  // Aplicar meta tags dinâmicas (título e favicon)
  useEffect(() => {
    // Atualizar título da página
    if (settings?.pageTitle) {
      document.title = settings.pageTitle;
    }

    // Atualizar favicon
    if (settings?.faviconUrl) {
      // Remover favicons existentes
      const existingFavicons = document.querySelectorAll('link[rel*="icon"]');
      existingFavicons.forEach(favicon => favicon.remove());

      // Adicionar novo favicon com cache busting
      const favicon = document.createElement('link');
      favicon.rel = 'icon';
      favicon.type = 'image/png';
      favicon.href = settings.faviconUrl + '?v=' + Date.now();
      document.head.appendChild(favicon);

      // Adicionar também como shortcut icon para compatibilidade
      const shortcutIcon = document.createElement('link');
      shortcutIcon.rel = 'shortcut icon';
      shortcutIcon.type = 'image/png';
      shortcutIcon.href = settings.faviconUrl + '?v=' + Date.now();
      document.head.appendChild(shortcutIcon);
    }
  }, [settings?.pageTitle, settings?.faviconUrl]);

  // Remove any banners dynamically - more specific targeting
  useEffect(() => {
    const removeBanners = () => {
      // Remove elements with specific version banner text
      const elements = Array.from(document.querySelectorAll('*')).filter(el =>
        el.textContent && el.textContent.includes('BUILD 2025-09-17-19:02')
      );
      elements.forEach(el => {
        el.remove();
      });
    };

    // Run on mount
    removeBanners();

    // Run every 5 seconds (less aggressive)
    const interval = setInterval(removeBanners, 5000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  if (!isAuthenticated) {
    return (
      <Suspense fallback={fallback}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Navigation />

      <main className="main-content flex-1 flex flex-col">
        <Suspense fallback={fallback}>
          <Routes>
            <Route path="/login" element={<Navigate to={isAdminUser ? "/admin/dashboard" : "/dashboard"} replace />} />
            <Route path="/" element={<Navigate to={isAdminUser ? "/admin/dashboard" : "/dashboard"} replace />} />
            <Route
              path="/contatos"
              element={
                <ProtectedRoute>
                  <ContactsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/whatsapp"
              element={
                <ProtectedRoute>
                  <WhatsAppConnectionsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/atendimento"
              element={
                <ProtectedRoute superAdminOnly={true}>
                  <AtendimentoPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/campanhas"
              element={
                <ProtectedRoute>
                  <CampaignsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/perfil"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/configuracoes"
              element={
                <ProtectedRoute adminOnly={true}>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/usuarios"
              element={
                <ProtectedRoute adminOnly={true}>
                  <UsersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/organizacoes"
              element={
                <ProtectedRoute adminOnly={true}>
                  <OrganizationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute adminOnly={true}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <UserDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ajuda/conexao"
              element={
                <ProtectedRoute>
                  <ConnectionHelpPage />
                </ProtectedRoute>
              }
            />
          </Routes>
        </Suspense>
      </main>

      {/* Footer com versão */}
      <footer className="bg-white border-t border-gray-200 py-3 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center gap-2">
            <span>Feito por Quibt</span>
          </div>
          <div className="text-gray-400">
            © {new Date().getFullYear()} Todos os direitos reservados
          </div>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <AppContent />
          <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#1e293b',
              color: '#fff',
              borderRadius: '12px',
              fontSize: '14px',
              padding: '12px 16px',
            },
            success: {
              style: {
                background: '#10b981',
              },
            },
            error: {
              style: {
                background: '#ef4444',
              },
            },
          }}
          />
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
